import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { clientDashboardKeys } from './clientDashboards'
import { extractErrorMessage } from './ticketMutations'
import type { MeetingPrep } from '@/types/meetingPrep'

export const meetingPrepKeys = {
  prep: (companyId: string) => [...clientDashboardKeys.all, 'prep', companyId] as const,
}

export function useMeetingPrep(companyId: string) {
  return useQuery({
    queryKey: meetingPrepKeys.prep(companyId),
    queryFn: () => api<{ data: MeetingPrep }>(`/client-dashboards/${companyId}/prep`),
  })
}

// Keep the ticks on screen: they are the latest the user did, and a response can predate a tick still being saved.
const keepTicks = (incoming: { data: MeetingPrep }, current: { data: MeetingPrep } | undefined) => {
  const ticked = new Map((current?.data.sections ?? []).flatMap(section => section.items.map(item => [item.key, item.ticked] as const)))
  return {
    data: {
      ...incoming.data,
      sections: incoming.data.sections.map(section => ({ ...section, items: section.items.map(item => ({ ...item, ticked: ticked.get(item.key) ?? item.ticked })) })),
    },
  }
}

// Write the notes, or get the saved ones back when nothing has changed; the response replaces the view.
export function useGenerateMeetingPrep(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ force = false }: { force?: boolean }) =>
      api<{ data: MeetingPrep }>(`/client-dashboards/${companyId}/prep/generate`, { method: 'POST', body: { force } }),
    onSuccess: data => queryClient.setQueryData<{ data: MeetingPrep }>(meetingPrepKeys.prep(companyId), current => keepTicks(data, current)),
    onError: err => toast.error(extractErrorMessage(err, "Couldn't prepare the meeting")),
  })
}

const withTick = (data: { data: MeetingPrep } | undefined, key: string, ticked: boolean) =>
  data && {
    data: {
      ...data.data,
      sections: data.data.sections.map(section => ({ ...section, items: section.items.map(item => (item.key === key ? { ...item, ticked } : item)) })),
    },
  }

const withTicks = (data: { data: MeetingPrep } | undefined, ticks: string[]) =>
  data && {
    data: {
      ...data.data,
      sections: data.data.sections.map(section => ({ ...section, items: section.items.map(item => ({ ...item, ticked: ticks.includes(item.key) })) })),
    },
  }

// Tick straight away and save in the background; saves for one client run in turn, and the server's ticks win once a save settles.
export function useTickMeetingPrep(companyId: string) {
  const queryClient = useQueryClient()
  const key = meetingPrepKeys.prep(companyId)
  return useMutation({
    scope: { id: `prep-ticks:${companyId}` },
    mutationFn: (variables: { key: string; ticked: boolean }) =>
      api<{ data: { ticks: string[] } }>(`/client-dashboards/${companyId}/prep/ticks`, { method: 'PUT', body: variables }),
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey: key })
      queryClient.setQueryData<{ data: MeetingPrep }>(key, data => withTick(data, variables.key, variables.ticked))
    },
    onSuccess: response => queryClient.setQueryData<{ data: MeetingPrep }>(key, data => withTicks(data, response.data.ticks)),
    onError: (err, variables) => {
      queryClient.setQueryData<{ data: MeetingPrep }>(key, data => withTick(data, variables.key, !variables.ticked))
      toast.error(extractErrorMessage(err, "Couldn't save the tick"))
      return queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
